import Axios from '../../../../common/util/Axios';
import store from '../../../../store';
import { SET_ALERT_OPEN } from '../../../../common/actions/types';
import {
  SET_SUPPLIER_RETURNS,
  SET_SUPPLIER_RETURNS_FAILED,
  SET_SUPPLIER_RETURNS_LOADING,
  SET_SUPPLIER_RETURN_DOCUMENT,
  SET_WE_OWE,
} from './types';

const OFFLINE_MESSAGE = 'Operation failed. Please try again later.';

function notifyIfOffline(dispatch, err) {
  if (!err || !err.response) {
    dispatch({
      type: SET_ALERT_OPEN,
      payload: {
        isAlertOpen: true,
        alertType: 'danger',
        alertMessage: OFFLINE_MESSAGE,
      },
    });
  }
}

function notifyError(dispatch, err) {
  if (!err || !err.response) {
    notifyIfOffline(dispatch, err);
    return;
  }
  const message = err.response.data && err.response.data.error;
  if (!message) return;
  dispatch({
    type: SET_ALERT_OPEN,
    payload: {
      isAlertOpen: true,
      alertType: 'danger',
      alertMessage: message,
    },
  });
}

function scopeParams(extra) {
  const companyId = store.getState().authState.companyId;
  const branchId = store.getState().authState.branchId;
  return { companyId, branchId, ...extra };
}

let weOweSeq = 0;
let returnsSeq = 0;
let documentSeq = 0;

export const loadWeOwe = (supplierId) => async (dispatch) => {
  const seq = ++weOweSeq;
  const { branchId } = scopeParams();
  if (!branchId) {
    if (seq !== weOweSeq) return;
    dispatch({ type: SET_WE_OWE, payload: 0 });
    return;
  }
  dispatch({ type: SET_WE_OWE, payload: null });
  try {
    const res = await Axios.get('/api/dashboard/supplier-return/we-owe', {
      params: scopeParams({ supplierId }),
    });
    if (seq !== weOweSeq) return;
    dispatch({
      type: SET_WE_OWE,
      payload: Number(res.data?.data?.balance) || 0,
    });
  } catch (err) {
    if (seq !== weOweSeq) return;
    notifyIfOffline(dispatch, err);
    dispatch({ type: SET_WE_OWE, payload: null });
  }
};

export const loadReturns = (supplierId, filters = {}) => async (dispatch) => {
  const seq = ++returnsSeq;
  const { branchId } = scopeParams();
  if (!branchId) {
    if (seq !== returnsSeq) return;
    dispatch({ type: SET_SUPPLIER_RETURNS, payload: [] });
    return;
  }
  dispatch({ type: SET_SUPPLIER_RETURNS_LOADING });
  try {
    const res = await Axios.get('/api/dashboard/supplier-return/find-all', {
      params: scopeParams({ supplierId, ...filters }),
    });
    if (seq !== returnsSeq) return;
    dispatch({
      type: SET_SUPPLIER_RETURNS,
      payload: Array.isArray(res.data?.data) ? res.data.data : [],
    });
  } catch (err) {
    if (seq !== returnsSeq) return;
    notifyIfOffline(dispatch, err);
    dispatch({ type: SET_SUPPLIER_RETURNS_FAILED });
  }
};

export const loadReturnDocument = (id) => async (dispatch) => {
  const seq = ++documentSeq;
  const { branchId } = scopeParams();
  dispatch({ type: SET_SUPPLIER_RETURN_DOCUMENT, payload: null });
  if (!branchId) {
    return;
  }
  try {
    const res = await Axios.get(`/api/dashboard/supplier-return/${id}`, {
      params: scopeParams(),
    });
    if (seq !== documentSeq) return;
    dispatch({
      type: SET_SUPPLIER_RETURN_DOCUMENT,
      payload: res.data?.data || null,
    });
  } catch (err) {
    if (seq !== documentSeq) return;
    notifyIfOffline(dispatch, err);
    dispatch({ type: SET_SUPPLIER_RETURN_DOCUMENT, payload: null });
  }
};

export const fetchPostedReturnsForSource = (sourceType, sourceId) => async () => {
  const { branchId } = scopeParams();
  if (!branchId || !sourceId) {
    return [];
  }
  try {
    const res = await Axios.get('/api/dashboard/supplier-return/for-source', {
      params: scopeParams({ sourceType, sourceId }),
    });
    return Array.isArray(res.data?.data) ? res.data.data : [];
  } catch (err) {
    return [];
  }
};

export const createSupplierReturn = (supplierId) => async (dispatch) => {
  try {
    const res = await Axios.post(
      '/api/dashboard/supplier-return',
      { supplierId },
      { params: scopeParams() }
    );
    if (res.data && res.data.success && res.data.data) return res.data.data;
    return null;
  } catch (err) {
    notifyError(dispatch, err);
    return null;
  }
};

export const saveSupplierReturn = (id, body) => async (dispatch) => {
  try {
    const res = await Axios.put(`/api/dashboard/supplier-return/${id}`, body, {
      params: scopeParams(),
    });
    if (res.data && res.data.success && res.data.data) {
      dispatch({ type: SET_SUPPLIER_RETURN_DOCUMENT, payload: res.data.data });
      return res.data.data;
    }
    return null;
  } catch (err) {
    notifyError(dispatch, err);
    return null;
  }
};

export const cancelSupplierReturn = (id) => async (dispatch) => {
  try {
    const res = await Axios.delete(`/api/dashboard/supplier-return/${id}`, {
      params: scopeParams(),
    });
    return Boolean(res.data && res.data.success);
  } catch (err) {
    notifyError(dispatch, err);
    return false;
  }
};

function previewFailureMessage(err) {
  if (!err || !err.response) return OFFLINE_MESSAGE;
  const message = err.response.data && err.response.data.error;
  return message || null;
}

export const previewSupplierReturn = (id, lines) => async () => {
  try {
    const res = await Axios.post(
      `/api/dashboard/supplier-return/${id}/preview`,
      { lines },
      { params: scopeParams() }
    );
    return (res.data && res.data.data) || null;
  } catch (err) {
    return { previewFailed: true, message: previewFailureMessage(err) };
  }
};

export const recordMoneyRefund = (id, body) => async (dispatch) => {
  try {
    const res = await Axios.post(
      `/api/dashboard/supplier-return/${id}/money-refund`,
      body,
      { params: scopeParams() }
    );
    if (res.data && res.data.success && res.data.data) {
      dispatch({ type: SET_SUPPLIER_RETURN_DOCUMENT, payload: res.data.data });
      return res.data.data;
    }
    return null;
  } catch (err) {
    notifyError(dispatch, err);
    return null;
  }
};

export const postSupplierReturn = (id, body) => async (dispatch) => {
  try {
    const res = await Axios.post(`/api/dashboard/supplier-return/${id}/post`, body, {
      params: scopeParams(),
    });
    if (res.data && res.data.success && res.data.data) {
      dispatch({ type: SET_SUPPLIER_RETURN_DOCUMENT, payload: res.data.data });
      return res.data.data;
    }
    return null;
  } catch (err) {
    notifyError(dispatch, err);
    return null;
  }
};

export const loadReturnSources = (supplierId) => async (dispatch) => {
  try {
    const res = await Axios.get('/api/dashboard/supplier-return/sources', {
      params: scopeParams({ supplierId }),
    });
    return (res.data && res.data.data) || { invoices: [], lots: [] };
  } catch (err) {
    notifyError(dispatch, err);
    return null;
  }
};

export const searchReturnProducts = (keyword) => async (dispatch) => {
  try {
    const res = await Axios.get('/api/dashboard/products/search-by-code-name-barcode', {
      params: scopeParams({ keyword }),
    });
    return Array.isArray(res.data && res.data.data) ? res.data.data : [];
  } catch (err) {
    notifyError(dispatch, err);
    return [];
  }
};

export const fetchInvoiceStocks = (invoiceId) => async (dispatch) => {
  const { branchId } = scopeParams();
  if (!branchId || !invoiceId) {
    return [];
  }
  try {
    const res = await Axios.get('/api/dashboard/supplier-invoice/stocks', {
      params: scopeParams({ invoiceId }),
    });
    return Array.isArray(res.data?.data) ? res.data.data : [];
  } catch (err) {
    notifyIfOffline(dispatch, err);
    return [];
  }
};
